import { Injectable, inject, signal, computed, effect } from '@angular/core';
import {
  collection, doc, query, where, orderBy, onSnapshot, updateDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FirebaseService } from './firebase.service.js';

export interface MarkerSource {
  sourceId: string;
  name: string;
  createdAt?: any; // Timestamp
  pairedAt?: any | null;
  lastActiveAt?: any | null;
  revoked?: boolean;
}

export interface Marker {
  sourceId: string;
  markerId: string;
  time: string;     // local-time string ("14:32:45")
  utcTime: string;  // ISO UTC — used to map to VOD position
  tag: string;
  createdAt?: any;
}

/**
 * Marker that has been mapped onto the currently-loaded VOD's timeline.
 * vodPositionSec = (Date.parse(utcTime) - vod.created_at_ms) / 1000
 */
export interface VodMappedMarker extends Marker {
  vodPositionSec: number;
  sourceName: string;
}

export interface CreatePairingCodeResult {
  code: string;
  sourceId: string;
  expiresAt: number; // ms
}

@Injectable({ providedIn: 'root' })
export class MarkersService {
  private firebaseService = inject(FirebaseService);

  /** Active (non-revoked) marker-sources for the current Pro user. */
  markerSources = signal<MarkerSource[]>([]);

  /** sourceId → markers[] (sorted by utcTime desc). */
  markersBySource = signal<Map<string, Marker[]>>(new Map());

  /** sourceIds the user has toggled OFF via filter chips. */
  hiddenSourceIds = signal<Set<string>>(new Set());

  private sourcesUnsub: Unsubscribe | null = null;
  private markersUnsubs = new Map<string, Unsubscribe>();

  constructor() {
    // Subscribe / cleanup based on auth + Pro state.
    effect(() => {
      const uid = this.firebaseService.currentUser()?.uid;
      const isPro = !!this.firebaseService.userProfile()?.isPro
        || this.firebaseService.userProfile()?.role === 'admin';
      const fs = this.firebaseService.firestore;

      // Always tear down before re-subscribing — keeps state coherent on logout.
      this.cleanup();
      if (!uid || !isPro || !fs) return;

      const sourcesRef = collection(fs, 'users', uid, 'marker-sources');
      this.sourcesUnsub = onSnapshot(sourcesRef, (snap) => {
        const sources: MarkerSource[] = snap.docs
          .map((d) => d.data() as MarkerSource)
          .filter((s) => !s.revoked);
        this.markerSources.set(sources);

        const currentIds = new Set(sources.map((s) => s.sourceId));

        // Subscribe to markers for any new source.
        for (const s of sources) {
          if (!this.markersUnsubs.has(s.sourceId)) {
            this.subscribeMarkersForSource(uid, s.sourceId);
          }
        }
        // Tear down listeners for sources that disappeared (revoked/deleted).
        for (const [id, unsub] of this.markersUnsubs.entries()) {
          if (!currentIds.has(id)) {
            unsub();
            this.markersUnsubs.delete(id);
            this.markersBySource.update((map) => {
              const next = new Map(map);
              next.delete(id);
              return next;
            });
          }
        }
      }, (err) => {
        console.error('marker-sources onSnapshot error:', err);
      });
    });
  }

  private subscribeMarkersForSource(uid: string, sourceId: string): void {
    const fs = this.firebaseService.firestore;
    if (!fs) return;
    const markersQ = query(
      collection(fs, 'users', uid, 'markers'),
      where('sourceId', '==', sourceId),
      orderBy('utcTime', 'desc'),
    );
    const unsub = onSnapshot(markersQ, (snap) => {
      const markers = snap.docs.map((d) => d.data() as Marker);
      this.markersBySource.update((map) => {
        const next = new Map(map);
        next.set(sourceId, markers);
        return next;
      });
    }, (err) => {
      console.error(`markers onSnapshot error (sourceId=${sourceId}):`, err);
    });
    this.markersUnsubs.set(sourceId, unsub);
  }

  /**
   * Returns markers that fall within the loaded VOD's wall-clock window,
   * mapped to VOD-relative seconds. Excludes markers from hidden sources.
   * Pass `vod.created_at` parsed to ms and the duration from Twitch API.
   */
  markersForVod(vodCreatedAtMs: number, vodDurationSec: number) {
    return computed<VodMappedMarker[]>(() => {
      if (!vodCreatedAtMs || vodDurationSec <= 0) return [];
      const hidden = this.hiddenSourceIds();
      const sources = new Map(this.markerSources().map((s) => [s.sourceId, s]));
      const vodEndMs = vodCreatedAtMs + vodDurationSec * 1000;
      const out: VodMappedMarker[] = [];

      for (const [sourceId, markers] of this.markersBySource().entries()) {
        if (hidden.has(sourceId)) continue;
        const sourceName = sources.get(sourceId)?.name ?? '—';
        for (const m of markers) {
          const ts = Date.parse(m.utcTime);
          if (Number.isNaN(ts)) continue;
          if (ts < vodCreatedAtMs || ts > vodEndMs) continue;
          out.push({
            ...m,
            sourceName,
            vodPositionSec: (ts - vodCreatedAtMs) / 1000,
          });
        }
      }
      out.sort((a, b) => a.vodPositionSec - b.vodPositionSec);
      return out;
    });
  }

  /** Toggle filter visibility for a source. */
  toggleSourceHidden(sourceId: string): void {
    this.hiddenSourceIds.update((set) => {
      const next = new Set(set);
      if (next.has(sourceId)) next.delete(sourceId);
      else next.add(sourceId);
      return next;
    });
  }

  /** Owner-side: invokes the Cloud Function to generate a pairing code. */
  async createPairingCode(name: string): Promise<CreatePairingCodeResult> {
    const fns = this.firebaseService.functions;
    if (!fns) throw new Error('Firebase Functions not initialized.');
    const fn = httpsCallable<{ name: string }, CreatePairingCodeResult>(
      fns, 'createPairingCode',
    );
    const { data } = await fn({ name });
    return data;
  }

  /** Owner-side: revoke a paired source. Existing markers stay; new writes denied. */
  async revokeSource(sourceId: string): Promise<void> {
    const uid = this.firebaseService.currentUser()?.uid;
    const fs = this.firebaseService.firestore;
    if (!uid || !fs) return;
    await updateDoc(doc(fs, 'users', uid, 'marker-sources', sourceId), {
      revoked: true,
    });
  }

  /** Owner-side: rename a source. */
  async renameSource(sourceId: string, newName: string): Promise<void> {
    const uid = this.firebaseService.currentUser()?.uid;
    const fs = this.firebaseService.firestore;
    if (!uid || !fs) return;
    const trimmed = newName.trim().slice(0, 50);
    if (!trimmed) return;
    await updateDoc(doc(fs, 'users', uid, 'marker-sources', sourceId), {
      name: trimmed,
    });
  }

  private cleanup(): void {
    this.sourcesUnsub?.();
    this.sourcesUnsub = null;
    for (const unsub of this.markersUnsubs.values()) unsub();
    this.markersUnsubs.clear();
    this.markerSources.set([]);
    this.markersBySource.set(new Map());
    this.hiddenSourceIds.set(new Set());
  }
}
